'use client'

import { useState } from 'react'

import { Box, SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Tooltip } from '../Tooltip'
import {
  SIZE,
  UserAvatarSize,
  avatarTypographyStyles,
  getBg,
  pulse,
} from './UserAvatar.styles'

export type { UserAvatarSize }

export interface UserAvatarIProps {
  name: string | null
  size?: UserAvatarSize
  colorScheme?: ColorScheme
  /** Shows a pulsing skeleton placeholder while true */
  isLoading?: boolean
  /** Overrides the circle fill. */
  bgColor?: string
  /** Overrides the initials color. See `bgColor`. */
  textColor?: string
  /** Optional Design Library text style for the initials. */
  textStyle?: SystemStyleObject['textStyle']
}

const getInitials = (name: string | null): string => {
  if (!name?.trim()) return 'N/A'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return (parts[0][0] ?? '').toUpperCase()
  return (
    (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
  ).toUpperCase()
}

export const UserAvatar = ({
  name,
  size = 'sm',
  colorScheme = 'dark',
  isLoading = false,
  bgColor,
  textColor,
  textStyle,
}: UserAvatarIProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const { diameter } = SIZE[size]
  const bg = bgColor ?? getBg(colorScheme)
  const typographyStyles = avatarTypographyStyles(size, textStyle)

  if (isLoading) {
    return (
      <Box
        animation={`${pulse} 1.2s ease-in-out infinite`}
        aria-busy
        bg={bg}
        borderRadius='999px'
        data-testid='user-avatar-loading'
        flexShrink={0}
        h={diameter}
        w={diameter}
      />
    )
  }

  return (
    <>
      <Box
        alignItems='center'
        bg={bg}
        borderRadius='999px'
        color={textColor ?? 'text.on-accent'}
        display='inline-flex'
        flexShrink={0}
        h={diameter}
        justifyContent='center'
        letterSpacing={0}
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setAnchorEl(null)}
        overflow='hidden'
        userSelect='none'
        w={diameter}
        {...typographyStyles}
      >
        {getInitials(name)}
      </Box>
      {anchorEl && name && (
        <Tooltip anchorEl={anchorEl} colorScheme={colorScheme} label={name} />
      )}
    </>
  )
}
